/* eslint-disable pec/import-graphql-hooks-from-local */
import {
  LazyQueryExecFunction,
  LazyQueryHookOptions as OptionsType,
  OperationVariables,
  QueryResult,
  useLazyQuery as useApolloLazyQuery,
} from '@apollo/client'
import { isAbortError, useErrorHandler } from 'components/error'
import { DocumentNode } from 'graphql'
import { useCallback, useEffect } from 'react'

import { useAbortSignal } from './useAbortSignal'

// Override useLazyQuery from @apollo/client to use custom defaults

export function useLazyQuery<TData = any, TVariables = OperationVariables>(
  query: DocumentNode,
  options: OptionsType<TData, TVariables> = {}
): LazyQueryResultArray<TData, TVariables> {
  const handleError = useErrorHandler()

  const [execute, result] = useApolloLazyQuery<TData, TVariables>(query, {
    fetchPolicy: 'network-only',
    ...options,
  })

  const { abort, controllerRef } = useAbortSignal()

  const executeWithSignal: LazyQueryExecFunction<TData, TVariables> = useCallback(
    (options) =>
      execute({
        context: {
          fetchOptions: {
            signal: controllerRef.current.signal,
          },
        },
        ...options,
      }),
    [execute, controllerRef]
  )

  useEffect(() => {
    if (result.error && !isAbortError(result.error)) {
      handleError(result.error)
    }
  }, [result.error, handleError])

  return [executeWithSignal, { ...result, abort, aborted: isAbortError(result.error) }]
}

export type LazyQueryHookOptions<TData = any, TVariables = OperationVariables> = OptionsType<TData, TVariables>

export type LazyQueryResultArray<TData = any, TVariables = OperationVariables> = [
  LazyQueryExecFunction<TData, TVariables>,
  QueryResult<TData, TVariables> & { aborted: boolean; abort: () => void }
]
